import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  PageHeader,
  Pagination,
  StatTile,
  useToast,
} from '@/components/ui';
import { ConnectionForm } from '@/features/ats/ConnectionForm';
import { HandoverHistory } from '@/features/ats/HandoverHistory';
import { PendingCandidates } from '@/features/ats/PendingCandidates';
import { connectionLabel, connectionTone, stageLabel } from '@/features/ats/stages';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/**
 * Where this company's candidates go when they leave the platform.
 *
 * The screen never claims something happened that the API did not say
 * happened: "sent to Greenhouse" and "your file is ready" are different
 * sentences, and which one appears comes from the response rather than from
 * an assumption made here.
 */
export function CompanyIntegrationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const overview = useQuery({
    queryKey: ['company-ats'],
    queryFn: companyApi.getIntegration,
  });
  const connection = overview.data?.connection ?? null;

  const pending = useQuery({
    queryKey: ['company-ats-pending'],
    queryFn: companyApi.listPendingHandover,
    enabled: Boolean(connection),
  });
  const handovers = useQuery({
    queryKey: ['company-ats-handovers', page],
    queryFn: () => companyApi.listHandovers({ page, page_size: 10 }),
    enabled: Boolean(connection),
  });

  function refresh() {
    ['company-ats', 'company-ats-pending', 'company-ats-handovers'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    );
  }

  const save = useMutation({
    mutationFn: companyApi.saveIntegration,
    onSuccess: () => {
      toast.success('Your integration is ready.');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const disconnect = useMutation({
    mutationFn: companyApi.disconnectIntegration,
    onSuccess: () => {
      setConfirmingDisconnect(false);
      toast.success('Your integration has been removed.');
      refresh();
    },
    onError: (error) => {
      setConfirmingDisconnect(false);
      toast.error(errorMessage(error));
    },
  });

  const test = useMutation({
    mutationFn: companyApi.testIntegration,
    onSuccess: (result) => {
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const handOver = useMutation({
    mutationFn: () => companyApi.exportCandidates(selected),
    onSuccess: (result) => {
      // Whatever the API says happened is what the company is told.
      toast.success(result.message);
      setSelected([]);
      setPage(1);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  async function download(batch) {
    setDownloading(batch.id);
    try {
      const link = await companyApi.getHandoverDownload(batch.id);
      window.open(resolveFileUrl(link.url), '_blank', 'noopener');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDownloading(null);
    }
  }

  if (overview.isPending) return <LoadingState label="Loading your integration…" />;
  if (overview.isError) {
    return (
      <ErrorState message={errorMessage(overview.error)} onRetry={() => void overview.refetch()} />
    );
  }

  const data = overview.data;
  const candidates = pending.data ?? [];

  return (
    <div>
      <PageHeader
        title="Integrations"
        description="Send your candidates to the system your hiring actually runs in."
      />

      {connection?.status === 'ERROR' && connection.last_error && (
        <Alert tone="warning" className="mb-6" title="Your integration needs attention">
          {connection.last_error}
        </Alert>
      )}

      {connection && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatTile label="Waiting" value={connection.pending_count} />
          <StatTile label="Sent so far" value={connection.exported_count} />
          <StatTile
            label="Sends"
            value={
              connection.auto_export && connection.automation_allowed
                ? 'Automatically'
                : 'When you ask'
            }
          />
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          {connection && (
            <Card>
              <CardHeader
                title="Waiting to go across"
                description={`Everyone ${stageLabel(connection.from_stage).toLowerCase()}, who has not been sent before.`}
              />
              {pending.isPending ? (
                <LoadingState label="Counting…" />
              ) : (
                <PendingCandidates
                  candidates={candidates}
                  selected={selected}
                  onToggle={(id) =>
                    setSelected((current) =>
                      current.includes(id)
                        ? current.filter((value) => value !== id)
                        : [...current, id],
                    )
                  }
                  onClear={() => setSelected([])}
                />
              )}
              {candidates.length > 0 && (
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Button isLoading={handOver.isPending} onClick={() => handOver.mutate()}>
                    {selected.length
                      ? `Hand over ${selected.length}`
                      : `Hand over all ${candidates.length}`}
                  </Button>
                  <p className="text-ink-muted text-sm">
                    Sending them by hand is free on every plan.
                  </p>
                </div>
              )}
            </Card>
          )}

          <ConnectionForm
            key={connection?.id ?? 'new'}
            providers={data.providers}
            connection={connection}
            defaultProvider={data.default_provider}
            automationAllowed={data.automation_allowed}
            automationNote={data.automation_note}
            planName={data.plan_name}
            isSaving={save.isPending}
            onSave={(input) => save.mutate(input)}
          />

          {connection && (
            <Card>
              <CardHeader title="Handovers" />
              {handovers.isPending ? (
                <LoadingState label="Loading handovers…" />
              ) : (
                <>
                  <HandoverHistory
                    batches={handovers.data?.data ?? []}
                    onDownload={download}
                    downloading={downloading}
                  />
                  {handovers.data?.meta && (
                    <Pagination
                      page={handovers.data.meta.page}
                      totalPages={handovers.data.meta.total_pages}
                      totalItems={handovers.data.meta.total_items}
                      onPageChange={setPage}
                    />
                  )}
                </>
              )}
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {connection ? (
            <Card>
              <CardHeader title="Connected to" />
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="bg-brand-50 text-brand-600 ring-brand-100 grid size-11 shrink-0 place-items-center rounded-xl ring-1 ring-inset"
                >
                  <Icon name="integrations" size={20} />
                </span>
                <div className="min-w-0">
                  <p className="text-ink text-[15px] leading-snug font-semibold">
                    {connection.provider_label}
                  </p>
                  <div className="mt-1.5">
                    <Badge dot tone={connectionTone(connection.status)}>
                      {connectionLabel(connection.status)}
                    </Badge>
                  </div>
                </div>
              </div>
              {connection.last_export_at && (
                <dl className="border-line mt-4 space-y-1 border-t pt-4 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-muted">Last handover</dt>
                    <dd className="text-ink">{formatDate(connection.last_export_at)}</dd>
                  </div>
                </dl>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  isLoading={test.isPending}
                  onClick={() => test.mutate()}
                >
                  Test connection
                </Button>
                <Button
                  variant="ghostDanger"
                  size="sm"
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  Disconnect
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <EmptyState
                compact
                icon={<Icon name="integrations" size={16} />}
                title="Nothing connected yet"
                description="Most teams already have somewhere their hiring happens. Connect it and candidates go across on their own, instead of being copied by hand."
              />
            </Card>
          )}

          <Card>
            <CardHeader title="What this costs" />
            <p className="text-ink-muted text-sm">{data.automation_note}</p>
            <p className="text-ink-muted mt-3 text-sm">
              Your own applicants are yours on any plan — taking them out is reading your own
              pipeline.
            </p>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingDisconnect}
        title="Disconnect your ATS?"
        message="The stored credentials and the record of what was handed over are both removed. Candidates already in your ATS stay where they are."
        confirmLabel="Disconnect"
        isLoading={disconnect.isPending}
        onCancel={() => setConfirmingDisconnect(false)}
        onConfirm={() => disconnect.mutate()}
      />
    </div>
  );
}
